import {
  ApplicationInsights,
  DistributedTracingModes,
  ITelemetryPlugin,
} from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { createBrowserHistory } from 'history';
import { v4 as uuidv4 } from 'uuid';
import Config from '../config';
import {
  APP_INSIGHTS_CLIENT_TYPE,
  APP_INSIGHTS_SERVICE_NAMES,
  APP_INSIGHTS_TAGS,
  TELEMETRY_STORAGE_KEYS,
  SESSION_PREFIX,
  CORRELATION_FIELDS,
  EXCLUDED_TELEMETRY_DOMAINS,
  STRING_CONSTANTS,
  APP_INSIGHTS_CLIENT_PLATFORM,
} from '../constants/telemetryConstants';
import { EMPTY_STRING } from '../constants/constant';

const reactPlugin = new ReactPlugin();
const browserHistory = createBrowserHistory();
const { APPINSIGHTS_CONNECTION_STRING } = Config;

const getClientInstallationId = (): string => {
  let installationId = localStorage.getItem(
    TELEMETRY_STORAGE_KEYS.CLIENT_INSTALLATION_ID,
  );
  if (!installationId) {
    installationId = uuidv4();
    localStorage.setItem(
      TELEMETRY_STORAGE_KEYS.CLIENT_INSTALLATION_ID,
      installationId,
    );
  }
  return installationId;
};

function getActiveFeatureFlags(): string[] {
  const flags = localStorage.getItem(TELEMETRY_STORAGE_KEYS.FEATURE_FLAGS);
  return flags ? JSON.parse(flags) : [];
}

function getCurrentUserId(): string | null {
  return window.sessionStorage.getItem(TELEMETRY_STORAGE_KEYS.USER_ID) ?? null;
}

function getCurrentOrganizationId(): string | null {
  return window.sessionStorage.getItem(TELEMETRY_STORAGE_KEYS.ORG_ID) ?? null;
}

const getSessionId = (): string => {
  let sessionId = window.sessionStorage.getItem(
    TELEMETRY_STORAGE_KEYS.SESSION_ID,
  );
  if (!sessionId) {
    sessionId = `${SESSION_PREFIX.WEB}-${Date.now()}-${uuidv4()}`;
    window.sessionStorage.setItem(TELEMETRY_STORAGE_KEYS.SESSION_ID, sessionId);
  }
  return sessionId;
};

function getSaaSContext() {
  return {
    userId: getCurrentUserId(),
    organizationId: getCurrentOrganizationId(),
  };
}

export const getClientPlatform = () =>
  navigator.userAgent.includes(STRING_CONSTANTS.MOBILE)
    ? APP_INSIGHTS_CLIENT_PLATFORM.MOBILE_BROWSER
    : APP_INSIGHTS_CLIENT_PLATFORM.DESKTOP_BROWSER;

const appInsights = new ApplicationInsights({
  config: {
    connectionString: APPINSIGHTS_CONNECTION_STRING ?? EMPTY_STRING,
    extensions: [reactPlugin as unknown as ITelemetryPlugin],
    extensionConfig: {
      [reactPlugin.identifier]: {
        history: browserHistory,
      },
    },
    enableAutoRouteTracking: true,
    enableCorsCorrelation: true,
    enableRequestHeaderTracking: true,
    enableResponseHeaderTracking: true,
    correlationHeaderExcludedDomains: [
      EXCLUDED_TELEMETRY_DOMAINS.AZURE_BLOB,
      EXCLUDED_TELEMETRY_DOMAINS.ZOHO_SALESIQ,
      EXCLUDED_TELEMETRY_DOMAINS.ZOHO_DESK,
    ],
    autoTrackPageVisitTime: true,
    enableUnhandledPromiseRejectionTracking: true,
    disableFetchTracking: false,
    disableAjaxTracking: false,
    distributedTracingMode: DistributedTracingModes.AI_AND_W3C,
    disableExceptionTracking: false,
    maxBatchInterval: 1000,
  },
});

const initializeAppInsights = () => {
  try {
    appInsights.loadAppInsights();

    // Add telemetry initializer to exclude Zoho SalesIQ/Desk requests
    appInsights.addTelemetryInitializer((envelope) => {
      // Filter out dependency calls to Zoho SalesIQ
      if (envelope.baseType === 'RemoteDependencyData') {
        const target = envelope.baseData?.target;
        if (
          target?.includes('zohopublic.com') ||
          target?.includes('zoho.com')
        ) {
          return false; // Don't track this telemetry
        }
      }

      return true; // Continue with normal processing for other requests
    });

    appInsights.addTelemetryInitializer((envelope) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const envelopeAny = envelope;

      envelopeAny.tags = { ...(envelopeAny.tags ?? {}) };
      envelopeAny.tags[APP_INSIGHTS_TAGS.CLOUD_ROLE] =
        APP_INSIGHTS_SERVICE_NAMES.WEB_CLIENT;
      envelopeAny.tags[APP_INSIGHTS_TAGS.CLOUD_ROLE_INSTANCE] =
        window.location.hostname;
      envelopeAny.tags[APP_INSIGHTS_TAGS.SESSION_ID] = getSessionId();
      envelopeAny.tags[APP_INSIGHTS_TAGS.USER_ID] = getCurrentUserId();

      const data = { ...(envelopeAny.data ?? {}) };
      const clientInstallationId = getClientInstallationId();
      data[CORRELATION_FIELDS.CLIENT_ID] = clientInstallationId;
      data.clientInstallationId = clientInstallationId;
      data[CORRELATION_FIELDS.SESSION_ID] = getSessionId();
      data.clientVersion = Config.APP_VERSION;
      data.clientType = APP_INSIGHTS_CLIENT_TYPE.WEB;
      data.clientPlatform = getClientPlatform();
      data.serviceVersion = Config.APP_VERSION;
      data.environment = Config.ENVIRONMENT;
      data.region = Config.REGION;

      const saasContext = getSaaSContext();
      if (saasContext) {
        data[CORRELATION_FIELDS.USER_ID] = saasContext.userId;
        data[CORRELATION_FIELDS.ORG_ID] = saasContext.organizationId;
      }

      const activeFeatureFlags = getActiveFeatureFlags();
      if (activeFeatureFlags && activeFeatureFlags.length > 0) {
        data.featureFlags = activeFeatureFlags.join(',');
      }
      envelopeAny.data = data;

      return true;
    });
  } catch (error) {
    void error;
  }
};

export {
  appInsights,
  reactPlugin,
  browserHistory,
  getClientInstallationId,
  getSessionId,
  initializeAppInsights,
};
